import { CharacterType, type ICharacter, type IPersona, useAppDispatch, usePersona } from "@next-platform/client-common-core";
import type RulebookAttachTarget from "@next-platform/client-common-core/rulebook/attach/RulebookAttachTarget";
import useRulebookAttachSubmit from "@next-platform/client-common-core/rulebook/attach/useRulebookAttachSubmit";
import { getPersonas } from "@next-platform/client-common-core/userPersona/personaSlice";
import getCharacterAttachBlockedStatus from "@next-platform/client-common-core/utils/getCharacterAttachBlockedStatus";
import { type RulebookOwnerType } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FilterKeys, SortOptions } from "@/components/type";
import { useMyChatbotsData } from "@/hooks";
import { convertToSnakeCase } from "@/utils";

import useRulebookAttachNotices from "./useRulebookAttachNotices";

export type RulebookAttachOwnerKind = "characters" | "personas";

/** Character and persona ids can collide — attach markers are scoped per tab. */
const attachedKey = (kind: RulebookAttachOwnerKind, id: string): string => `${kind}:${id}`;

const mapCharacterTargets = (chatbots: ICharacter[] | null | undefined, attachedIds: Record<string, string>): RulebookAttachTarget[] =>
  (chatbots ?? []).map((character) => ({
    id: character.id,
    name: character.name,
    ...(character.avatar_url !== undefined && { avatarUrl: character.avatar_url }),
    attachedRulebookId:
      attachedIds[attachedKey("characters", character.id)] ?? character.rulebook_id ?? (character.has_rulebook ? "__unknown__" : null),
    attachedRulebookName: character.rulebook?.rulebook.name ?? null,
  }));

const mapPersonaTargets = (personas: IPersona[] | undefined, search: string, attachedIds: Record<string, string>): RulebookAttachTarget[] => {
  const query = search.trim().toLowerCase();
  return (personas ?? [])
    .filter((persona) => !query || persona.name.toLowerCase().includes(query))
    .map((persona) => ({
      id: persona.id,
      name: persona.name,
      ...(persona.avatar_url !== undefined && { avatarUrl: persona.avatar_url }),
      attachedRulebookId: attachedIds[attachedKey("personas", persona.id)] ?? persona.rulebook_id ?? null,
      attachedRulebookName: persona.rulebook_hud?.rulebook.name ?? null,
    }));
};

type RulebookAttachTargetsProps = {
  isOpen: boolean;
  rulebookId: string;
  onClose: () => void;
  canAttachCharacters: boolean;
  canAttachPersonas: boolean;
  /** Called after a successful attach (e.g. to refresh profile counts). */
  onAttached?: () => void;
};

type AttachTargetRefreshProps = {
  isOpen: boolean;
  defaultOwnerKind: RulebookAttachOwnerKind;
  ownerKind: RulebookAttachOwnerKind;
  search: string;
  canAttachPersonas: boolean;
  chatbotData: ReturnType<typeof useMyChatbotsData>;
};

/** Open-time refresh (re-list characters + personas) plus the error flags/retry for the characters tab. */
const useAttachTargetRefresh = ({ isOpen, defaultOwnerKind, ownerKind, search, canAttachPersonas, chatbotData }: AttachTargetRefreshProps) => {
  const dispatch = useAppDispatch();
  const { refetch, handleLocalSearch } = chatbotData;
  // The mount fetch already covers the first open — only refresh on later re-opens.
  const refHasOpenedOnce = useRef(false);

  /** Refetches the character list and (when allowed) the persona list each time the modal re-opens. */
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (refHasOpenedOnce.current) {
      void refetch();
    }
    refHasOpenedOnce.current = true;
    // usePersona only reads what the login middleware loaded — refresh so the list is current.
    if (canAttachPersonas) {
      void dispatch(getPersonas());
    }
  }, [isOpen, defaultOwnerKind, refetch, dispatch, canAttachPersonas]);

  // Only the Characters tab drives the server-mode character pipeline — a persona query must not
  // refetch the character list. Tab switches clear the search, so the pushed value stays "" there.
  useEffect(() => {
    if (ownerKind !== "characters") {
      return;
    }
    handleLocalSearch(search);
  }, [handleLocalSearch, search, ownerKind]);

  // The persona slice drops the error on rejection (loading only) — only the characters tab can report one.
  // No loaded items means the initial fetch failed; items with an error mean a load-more/sort failed.
  const isInitialError = ownerKind === "characters" && chatbotData.error != null && (chatbotData.filteredAndSortedChatbots?.length ?? 0) === 0;
  const isLoadMoreError = ownerKind === "characters" && chatbotData.error != null && !isInitialError;
  const retry = useCallback(() => {
    if (ownerKind === "characters") {
      refetch().catch(() => {});
      return;
    }
    void dispatch(getPersonas());
  }, [ownerKind, refetch, dispatch]);

  return { isInitialError, isLoadMoreError, retry };
};

/** Selection-derived flags: which target is picked, whether it's blocked, already attached, or a replace. */
const resolveSelection = ({
  targets,
  chatbots,
  ownerKind,
  selectedId,
  rulebookId,
}: {
  targets: RulebookAttachTarget[];
  chatbots: ICharacter[] | null | undefined;
  ownerKind: RulebookAttachOwnerKind;
  selectedId: string | null;
  rulebookId: string;
}) => {
  const selectedTarget = targets.find((target) => target.id === selectedId) ?? null;
  const selectedBlockedStatus = getCharacterAttachBlockedStatus((chatbots ?? []).find((character) => character.id === selectedTarget?.id));
  const ownerType: RulebookOwnerType = ownerKind === "characters" ? "character" : "persona";
  const isAlreadyAttached = selectedTarget?.attachedRulebookId === rulebookId;
  return {
    selectedTarget,
    selectedBlockedStatus,
    ownerType,
    isSelectedBlocked: ownerType === "character" && selectedBlockedStatus !== "none",
    isAlreadyAttached,
    isReplacing: selectedTarget !== null && selectedTarget.attachedRulebookId !== null && !isAlreadyAttached,
  };
};

/** Tab/search/selection state: resets on open, and switching tabs clears the search. */
const useAttachTargetTabs = ({ isOpen, defaultOwnerKind }: { isOpen: boolean; defaultOwnerKind: RulebookAttachOwnerKind }) => {
  const [stateOwnerKind, setStateOwnerKind] = useState<RulebookAttachOwnerKind>(defaultOwnerKind);
  const [stateSearch, setStateSearch] = useState("");
  const [stateSelectedId, setStateSelectedId] = useState<string | null>(null);

  const handleOwnerKindChange = useCallback((kind: RulebookAttachOwnerKind) => {
    setStateOwnerKind(kind);
    setStateSearch("");
    setStateSelectedId(null);
  }, []);

  /** Selection/search reset on open (the character fetch itself is in useAttachTargetRefresh). */
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setStateOwnerKind(defaultOwnerKind);
    setStateSearch("");
    setStateSelectedId(null);
  }, [isOpen, defaultOwnerKind]);

  return {
    ownerKind: stateOwnerKind,
    search: stateSearch,
    setSearch: setStateSearch,
    selectedId: stateSelectedId,
    setSelectedId: setStateSelectedId,
    handleOwnerKindChange,
  };
};

/** Bots tab count only when the character list is complete and unfiltered. The hook's own state is
 * the gate — the modal's search is shared with the personas tab, and `isFiltering` covers the
 * debounce gap where the input has moved but the (still unfiltered) page has not yet refetched. */
const numCharactersForCompleteList = (
  chatbotData: Pick<
    ReturnType<typeof useMyChatbotsData>,
    "chatbots" | "isFiltering" | "localFilterBy" | "localSearchQuery" | "shouldShowLoadMoreButton"
  >,
): number | null =>
  chatbotData.localSearchQuery.trim() !== "" ||
  chatbotData.localFilterBy !== FilterKeys.ALL ||
  chatbotData.isFiltering ||
  chatbotData.shouldShowLoadMoreButton
    ? null
    : (chatbotData.chatbots?.length ?? null);

/** Backing state for the attach modal: target lists (own standard characters and personas) + selection. */
export default function useRulebookAttachTargets({
  isOpen,
  rulebookId,
  onClose,
  canAttachCharacters,
  canAttachPersonas,
  onAttached,
}: RulebookAttachTargetsProps) {
  const { personas, loading: personasLoading } = usePersona();

  const defaultOwnerKind: RulebookAttachOwnerKind = canAttachCharacters ? "characters" : "personas";
  const {
    ownerKind: stateOwnerKind,
    search: stateSearch,
    setSearch: setStateSearch,
    selectedId: stateSelectedId,
    setSelectedId: setStateSelectedId,
    handleOwnerKindChange,
  } = useAttachTargetTabs({ isOpen, defaultOwnerKind });
  const [stateAttachedIds, setStateAttachedIds] = useState<Record<string, string>>({});

  const chatbotData = useMyChatbotsData({
    initialSortType: convertToSnakeCase(SortOptions.LATEST),
    type: CharacterType.STANDARD,
    isFetchEnabled: canAttachCharacters,
  });

  const characterTargets = useMemo(
    () => mapCharacterTargets(chatbotData.filteredAndSortedChatbots, stateAttachedIds),
    [chatbotData.filteredAndSortedChatbots, stateAttachedIds],
  );

  const personaTargets = useMemo(() => mapPersonaTargets(personas, stateSearch, stateAttachedIds), [personas, stateSearch, stateAttachedIds]);

  const targets = stateOwnerKind === "characters" ? characterTargets : personaTargets;
  const { selectedTarget, selectedBlockedStatus, ownerType, isSelectedBlocked, isAlreadyAttached, isReplacing } = resolveSelection({
    targets,
    chatbots: chatbotData.filteredAndSortedChatbots,
    ownerKind: stateOwnerKind,
    selectedId: stateSelectedId,
    rulebookId,
  });

  const { notifySucceeded, notifyFailed } = useRulebookAttachNotices();
  const markAttached = useCallback(
    (targetId: string) => {
      setStateAttachedIds((previous) => ({ ...previous, [attachedKey(stateOwnerKind, targetId)]: rulebookId }));
      onAttached?.();
    },
    [stateOwnerKind, rulebookId, onAttached],
  );

  const { isAttaching, handleSubmit } = useRulebookAttachSubmit({
    rulebookId,
    ownerType,
    selectedTarget,
    isAlreadyAttached,
    isSelectedBlocked,
    onAttached: markAttached,
    onClose,
    onSucceeded: notifySucceeded,
    onFailed: notifyFailed,
  });
  const { isInitialError, isLoadMoreError, retry } = useAttachTargetRefresh({
    isOpen,
    defaultOwnerKind,
    ownerKind: stateOwnerKind,
    search: stateSearch,
    canAttachPersonas,
    chatbotData,
  });

  return {
    ownerKind: stateOwnerKind,
    setOwnerKind: handleOwnerKindChange,
    search: stateSearch,
    setSearch: setStateSearch,
    targets,
    selectedTarget,
    isAlreadyAttached,
    isReplacing,
    selectedBlockedStatus,
    isSelectedBlocked,
    isAttaching,
    isLoading: stateOwnerKind === "characters" ? chatbotData.isLoading || chatbotData.isFiltering : (personasLoading?.isLoading ?? false),
    isInitialError,
    isLoadMoreError,
    retry,
    hasMoreCharacters: chatbotData.shouldShowLoadMoreButton,
    loadMoreCharacters: chatbotData.loadMoreChatbots,
    isLoadingMoreCharacters: chatbotData.isLoadingMoreGroupchats,
    numCharacters: numCharactersForCompleteList(chatbotData),
    numPersonas: personas?.length ?? null,
    handleSelectTarget: setStateSelectedId,
    handleSubmit,
  };
}
